import React from 'react';

import { imgPath } from '~common/utilities/ImgUrl';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { ConnectionDisplay } from '~src/spacesPlatform/components/ListView/DataConnectionComponents';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { EntityLinkProps } from '../../../app/spaces/Tooltips/Elements/EntityLink';
import { LocationField } from '../../../app/spaces/Tooltips/Elements/LocationField';
import { Name } from '../../../app/spaces/Tooltips/Elements/Name';
import { RelatedEntity } from '../../../app/spaces/Tooltips/Elements/RelatedEntity';
import { RelatedEntityList } from '../../../app/spaces/Tooltips/Elements/RelatedEntityList';
import { Tags } from '../../../app/spaces/Tooltips/Elements/Tags';
import { Tooltip } from '../../../app/spaces/Tooltips/Elements/Tooltip';
import { User } from '../../../app/spaces/Tooltips/Elements/User';
import { ContentTooltipProps, getPersonalSpaceLink,getProjectLink, TooltipDescriptionTestId } from '../../../app/spaces/Tooltips/TooltipHelper';
import { Messages } from '../../../codegen/Localize';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IDataSourceInfo } from '../../models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../models/mappers/CommonDatasourceInfo';

export function DatasourceTooltip(props: ContentTooltipProps): JSX.Element {
  const relatedConnections = getRelatedConnectionLinkProps(props.content, props.urls);
  const extraDetail = getCommonDatasourceInfo(props.content);
  const certDetails = extraDetail ? (extraDetail.certificationDetails || null) : null;
  const hasCertDetails = certDetails;
  const isPublished = DataSources.isPublished(extraDetail);
  const isEmbedded = DataSources.isEmbedded(extraDetail);

  const workbookLink: EntityLinkProps = extraDetail.workbook ? {
    href: props.urls.workbookUrl(extraDetail.workbook.id),
    iconImagePath: imgPath('workbook_content_type_small.svg'),
    iconHeightOverride: 18,  // the icon height is 18 just for workbooks :|
    name: extraDetail.workbook.name
  } : null;

  const projectLink = getProjectLink((props.content as IDataSourceInfo).project, props.urls);
  const personalSpaceLink = getPersonalSpaceLink(props.urls);
  const locationType = (props.content as IDataSourceInfo).location?.type;
  const ownerId = props.content.owner?.id;

  return (
    <Tooltip>
      <Name name={props.content.name} />
      <MarkupField markup={props.content.description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <RelatedEntityList label={Messages.column_connects_to()} list={relatedConnections} />
      { isEmbedded && <RelatedEntity label={Messages.datasourceCardWorkbook()} link={workbookLink} />}
      <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace}
        locationType={locationType} projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId}/>
      { isPublished && <User user={props.content.owner} label={Messages.owner()} urls={props.urls} /> }
      { FeatureFlags.modifiedByUserEnabled &&  isPublished && props.content.modifiedBy!== undefined &&
      <User user={props.content.modifiedBy} label={Messages.column_modified_by()} urls={props.urls} /> }
      { hasCertDetails && hasCertDetails.isCertified && (
        <div className={`${DetailsTooltipStyles.MetadataSection} ${DetailsTooltipStyles.MetadataSectionWithCertifierDetails}`}>
          <User user={UserMapper.mapOne(certDetails.certifier)} label={Messages.datasourceCardCertifier()} urls={props.urls} />
          <MarkupField label={Messages.dataSourceCertification_notes()} markup={certDetails.certificationNote} />
        </div>
      )}
    </Tooltip>
  );
}

function getRelatedConnectionLinkProps(datasource: IContentInfo, urls: SpacesSiteUrls): EntityLinkProps[] {
  if (datasource && datasource.hasOwnProperty('connections')) {
    const connections: IDataConnectionInfo[] = (datasource as any).connections;
    return connections.map(c => {
      const displayMetadata = ConnectionDisplay.getDisplayMetadata(c, urls);
      const translatedText = ConnectionDisplay.translatedText(displayMetadata);
      return {
        name: translatedText.connectionName,
        href: displayMetadata.href,
        iconImagePath: displayMetadata.iconImageUrl
      };
    });
  } else {
    return [];
  }
}
